type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  metadata?: Record<string, any>;
  details?: any[];
}

const serverLogs: LogEntry[] = [];
const logStats: Record<LogLevel, number> = {
  info: 0,
  warn: 0,
  error: 0,
  debug: 0,
};

function writeLog(entry: LogEntry): void {
  serverLogs.push(entry);
  logStats[entry.level] += 1;
  console.log(JSON.stringify(entry));
}

export class ServerLogger {
  private context: string;

  constructor(context: string = 'Node') {
    this.context = context;
  }

  private logInternal(
    level: LogLevel,
    message: string,
    meta: Record<string, any> | null = null,
    ...args: any[]
  ): void {
    const hasMeta = meta && typeof meta === 'object' && Object.keys(meta).length > 0;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context: this.context,
      message,
      ...(hasMeta ? { metadata: meta } : {}),
      ...(args.length > 0
        ? { details: args.map((arg) => (typeof arg === 'object' ? arg : String(arg))) }
        : {}),
    };
    writeLog(entry);
  }

  log(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('info', message, meta, ...args);
  }

  info(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('info', message, meta, ...args);
  }

  warn(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('warn', message, meta, ...args);
  }

  error(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('error', message, meta, ...args);
  }

  debug(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('debug', message, meta, ...args);
  }

  imgLog(message: string, meta: Record<string, any> | null = null, ...args: any[]): void {
    this.logInternal('info', `IMG_LOG: ${message}`, meta, ...args);
  }
}

export const serverLogger = new ServerLogger('NextJS');

export function getServerLogs(limit?: number): string {
  const entries = limit ? serverLogs.slice(-limit) : serverLogs;
  return entries.map((e) => JSON.stringify(e)).join('\n');
}

export function clearServerLogs(): void {
  serverLogs.length = 0;
  (Object.keys(logStats) as LogLevel[]).forEach((level) => {
    logStats[level] = 0;
  });
}

export function getLogStats(): Record<LogLevel | 'total', number> {
  return { ...logStats, total: serverLogs.length };
}
